import { HttpClient } from '@angular/common/http';
import { Service, computed, effect, inject, signal } from '@angular/core';
import { Router } from '@angular/router';

import { environment } from '../../environments/environment';
import { MODULE_ROUTE_PREFIX } from '../core/modules/module-routes';
import { noLicenceUrl } from '../core/modules/module-url';
import {
  ServiceCatalogModule,
  ServiceCompanyAccess,
  ServiceModule,
} from '../core/umc/service-api.model';
import { CompanyContextService } from './company-context.service';

/**
 * A module's licensing verdict for one company and the signed-in person.
 * `unlicensed` and `noSeat` withhold the module; see `licenseStateOf`.
 */
export type LicenseState = 'licensed' | 'noSeat' | 'unlicensed' | 'none';

/** Whether a verdict withholds the module. */
export function isWithheld(state: LicenseState): boolean {
  return state === 'unlicensed' || state === 'noSeat';
}

/**
 * What the ACTIVE company is licensed for, from the User Management Center.
 *
 * Fetched from the platform catalogue — `GET /service/modules?companyId={id}`
 * — whenever the active company changes, cached per company for the session.
 * The catalogue lists every module that exists and, with a company named,
 * says per module whether that company holds a LIVE licence
 * (`heldByCompany`).
 *
 * A module's licensing IDENTITY is its `ModulePublicId` GUID: the shell's
 * environment maps module ids onto the target UMC's GUIDs
 * (`environment.umc.moduleIds`), because UMC mints them per database —
 * DEV and production carry different values. A module present in that map is
 * commercial; one absent is never gated. Once the company's catalogue answer
 * arrived, a commercial module is licensed or not — there is no third verdict:
 * a GUID the catalogue does not list counts as unlicensed and is reported in
 * the console. Only a missing answer (not loaded yet, failed call) gates
 * nothing: a UMC outage must not blank the dashboard.
 *
 * Mock mode builds the catalogue from the same environment map — company 1
 * holds every mapped module, company 2 none — so switching companies
 * demonstrates the gating without a running UMC.
 */
@Service()
export class LicenseContextService {
  private readonly http = inject(HttpClient);
  private readonly companies = inject(CompanyContextService);
  private readonly router = inject(Router);

  /** Catalogue answers by company id — an entry appears when its fetch settles. */
  private readonly byCompany = signal<ReadonlyMap<number, readonly ServiceCatalogModule[]>>(
    new Map(),
  );

  /** Companies whose fetch already ran (or failed) — never re-asked. */
  private readonly requested = new Set<number>();

  /** Companies whose catalogue call failed — settled, but with no answer to gate on. */
  private readonly failedCompanies = signal<ReadonlySet<number>>(new Set());

  /**
   * Whether the licensing verdict for the active company is final.
   *
   * False while `/service/me` is loading, or while the active company's
   * catalogue has not answered yet. Until then every verdict reads `none` —
   * open — so the dashboard, the sidebar and the route guard wait for this
   * rather than offering modules for a moment and taking them away again. A
   * failed call settles too: a UMC outage fails open, as before.
   */
  readonly settled = computed<boolean>(() => {
    const state = this.companies.loadState();

    if (state === 'loading') {
      return false;
    }

    // `idle` — nothing was asked for, so there is nothing to wait for;
    // `failed` — no answer will come.
    if (state !== 'ready' || this.companies.cardIncomplete()) {
      return true;
    }

    const companyId = this.companies.activeMembership()?.companyId;

    if (companyId === undefined) {
      return true;
    }

    return this.byCompany().has(companyId) || this.failedCompanies().has(companyId);
  });

  /** The active company's answer; null while none arrived (or the call failed). */
  private readonly activeCatalog = computed<readonly ServiceCatalogModule[] | null>(() => {
    const companyId = this.companies.activeMembership()?.companyId;

    return companyId === undefined ? null : (this.byCompany().get(companyId) ?? null);
  });

  /** Module GUIDs the active company holds a live licence for. */
  readonly licensedModuleIds = computed<ReadonlySet<string>>(() => {
    return new Set(
      (this.activeCatalog() ?? [])
        .filter((module) => module.heldByCompany)
        .map((module) => module.modulePublicId),
    );
  });

  constructor() {
    // Licenses follow the active company; `load` is idempotent per company.
    effect(() => {
      const companyId = this.companies.activeMembership()?.companyId;

      if (companyId !== undefined) {
        this.load(companyId);
      }
    });

    // The route guard covers NAVIGATION into a blocked module; this covers
    // STANDING in one when the verdict changes under the user — a company
    // switch, or the catalogue answer arriving late. The screen belongs to a
    // module the new company has no licence for, so the shell leaves it for
    // the "no licence" page (`blockedTarget`). Reacts to license state, not to
    // the URL: while the user merely navigates, the guard already answers.
    effect(() => {
      // Tracked unconditionally: on a run where the URL is outside modules, a
      // read hidden behind the condition would leave the effect with no
      // dependencies, and it would never wake again. The membership carries
      // the person's seats, the card flag withholds everything.
      this.licensedModuleIds();
      this.companies.activeMembership();
      this.companies.cardIncomplete();

      const moduleId = this.moduleIdFromUrl(this.router.url);

      if (moduleId && this.isBlocked(moduleId)) {
        void this.router.navigate(this.blockedTarget(moduleId));
      }
    });
  }

  /**
   * Where a withheld module leads instead of opening.
   *
   * The "no licence" page, which names the company and the way to UMC — before it the shell went
   * to the dashboard without a word, and a missing licence looked like a broken link. An
   * incomplete business card still goes to the dashboard: that withholds EVERY module, and the
   * dashboard is where the card is explained and opened.
   */
  blockedTarget(moduleId: string): string[] {
    if (this.companies.cardIncomplete()) {
      return ['/', MODULE_ROUTE_PREFIX];
    }

    return noLicenceUrl(moduleId);
  }

  /**
   * The module's licensing verdict for the active company and the signed-in
   * person.
   *
   * - `licensed` — the company holds a live licence on the module's GUID and
   *   the person is covered by it;
   * - `noSeat` — the company holds the licence, but the person is not on it:
   *   UMC's `/service/me` lists no access to the module for them. Since UMC
   *   spec 036 licences are sold per seat and per user, so a held licence no
   *   longer means everybody in the company may open the module. BLOCKS;
   * - `unlicensed` — the environment maps the module onto a GUID, the
   *   company's catalogue answer arrived, and no live licence covers it — a
   *   lapsed licence, a module never bought, or a GUID the catalogue does not
   *   list at all (warned in the console). BLOCKS;
   * - `none` — the environment maps no GUID (not a commercial module), or
   *   there is no answer yet (not loaded, failed call). No gating, no
   *   indicator: a UMC outage must not blank the dashboard.
   */
  licenseStateOf(moduleId: string): LicenseState {
    return this.verdict(this.activeCatalog(), this.companies.activeMembership(), moduleId);
  }

  /**
   * The active company's catalogue entry behind a module id — what the
   * licence-info dialog shows. `null` when the environment maps no GUID, no
   * catalogue answer arrived, or the catalogue does not know the GUID.
   */
  catalogEntryOf(moduleId: string): ServiceCatalogModule | null {
    const moduleGuid = environment.umc.moduleIds[moduleId];

    if (!moduleGuid) {
      return null;
    }

    return this.activeCatalog()?.find((module) => module.modulePublicId === moduleGuid) ?? null;
  }

  /** Licence rows by company — fetched on demand for the licence-info dialog. */
  private readonly detailsByCompany = signal<ReadonlyMap<number, readonly ServiceModule[]>>(
    new Map(),
  );

  private readonly detailsRequested = new Set<number>();

  /**
   * The licence behind a module for the ACTIVE company, from
   * `GET /service/companies/{id}/modules?includeInactive=true` — status,
   * seats, validity. `null` until `loadLicenseDetails()` ran and answered, or
   * when the company holds no licence at all. On-demand on purpose: the
   * dialog is the only consumer, gating needs none of it.
   */
  licenseDetailOf(moduleId: string): ServiceModule | null {
    const companyId = this.companies.activeMembership()?.companyId;
    const moduleCode = this.catalogEntryOf(moduleId)?.moduleCode;

    if (companyId === undefined || !moduleCode) {
      return null;
    }

    return (
      this.detailsByCompany()
        .get(companyId)
        ?.find((row) => row.moduleCode === moduleCode) ?? null
    );
  }

  /** Fetches the active company's licence rows once; safe to call on every dialog open. */
  loadLicenseDetails(): void {
    const companyId = this.companies.activeMembership()?.companyId;

    if (companyId === undefined || this.detailsRequested.has(companyId)) {
      return;
    }

    this.detailsRequested.add(companyId);

    if (environment.auth.useMockAuth) {
      this.storeDetails(companyId, buildMockDetails(companyId));

      return;
    }

    this.http
      .get<readonly ServiceModule[]>(
        `${environment.umc.baseUrl}/service/companies/${companyId}/modules?includeInactive=true`,
      )
      .subscribe({
        next: (rows) => this.storeDetails(companyId, rows),
        error: (error: unknown) => {
          // The dialog simply shows no licence row; not retried this session.
          console.error(`[umc] licence details for company ${companyId} failed.`, error);
        },
      });
  }

  private storeDetails(companyId: number, rows: readonly ServiceModule[]): void {
    this.detailsByCompany.update((map) => new Map(map).set(companyId, rows));
  }

  /**
   * Whether the shell must withhold this module. Every module is withheld
   * while the user's business card is incomplete: UMC refuses their calls
   * until it is filled in, so any module would open onto errors.
   */
  isBlocked(moduleId: string): boolean {
    if (this.companies.cardIncomplete()) {
      return true;
    }

    return isWithheld(this.licenseStateOf(moduleId));
  }

  /**
   * The same verdict as `licenseStateOf`, but for ANY company rather than the
   * active one — what the company switcher needs to grey out a company the
   * current module is not licensed for, BEFORE switching to it.
   *
   * Reads the per-company catalogue that is already cached; a company nobody
   * asked about yet answers `none`, which gates nothing. Call
   * `prefetchLicenses()` to have those answers ready.
   */
  licenseStateFor(companyId: number, moduleId: string): LicenseState {
    const catalog = this.byCompany().get(companyId) ?? null;
    const membership =
      this.companies.memberships().find((entry) => entry.companyId === companyId) ?? null;

    return this.verdict(catalog, membership, moduleId);
  }

  /** The one rule behind `licenseStateOf` and `licenseStateFor`. */
  private verdict(
    catalog: readonly ServiceCatalogModule[] | null,
    membership: ServiceCompanyAccess | null,
    moduleId: string,
  ): LicenseState {
    const moduleGuid = environment.umc.moduleIds[moduleId];

    if (!moduleGuid || !catalog) {
      return 'none';
    }

    const entry = catalog.find(
      (module) => module.modulePublicId.toLowerCase() === moduleGuid.toLowerCase(),
    );

    // A commercial module is either licensed or not. Absent from the
    // catalogue means nothing can be bought for it — no licence, and a
    // config mismatch worth seeing in the console rather than a silent pass.
    if (!entry) {
      warnUnknownGuid(moduleId, moduleGuid);

      return 'unlicensed';
    }

    if (!entry.heldByCompany) {
      return 'unlicensed';
    }

    // The company holds it; whether THIS person may use it is UMC's per-person
    // answer. No membership means no answer yet, which gates nothing.
    if (membership && !holdsSeat(membership, entry.moduleCode)) {
      return 'noSeat';
    }

    return 'licensed';
  }

  /**
   * Fetches the catalogue for companies the user has not switched to yet.
   *
   * Without this the switcher could only judge the company already active, and
   * a list where one row is greyed out only after you pick it would be worse
   * than no marking at all. Idempotent per company.
   */
  prefetchLicenses(companyIds: Iterable<number>): void {
    for (const companyId of companyIds) {
      this.load(companyId);
    }
  }

  /** The module a URL points into, or `null` on the shell's own screens. */
  private moduleIdFromUrl(url: string): string | null {
    const [, prefix, id] = url.split(/[?#]/)[0].split('/');

    return prefix === MODULE_ROUTE_PREFIX && id ? decodeURIComponent(id) : null;
  }

  private load(companyId: number): void {
    if (this.requested.has(companyId)) {
      return;
    }

    this.requested.add(companyId);

    if (environment.auth.useMockAuth) {
      this.store(companyId, buildMockCatalog(companyId));

      return;
    }

    this.http
      .get<readonly ServiceCatalogModule[]>(
        `${environment.umc.baseUrl}/service/modules?companyId=${companyId}`,
      )
      .subscribe({
        next: (catalog) => this.store(companyId, catalog),
        error: (error: unknown) => {
          // Stays out of `byCompany`: no data means no gating for this
          // company. Not retried this session — the next sign-in tries again.
          this.failedCompanies.update((failed) => new Set(failed).add(companyId));
          console.error(
            `[umc] module catalogue for company ${companyId} failed — no gating.`,
            error,
          );
        },
      });
  }

  private store(companyId: number, catalog: readonly ServiceCatalogModule[]): void {
    this.byCompany.update((map) => new Map(map).set(companyId, catalog));
  }
}

/** Modules already reported, so a re-render does not repeat the warning. */
const reportedUnknownGuids = new Set<string>();

function warnUnknownGuid(moduleId: string, moduleGuid: string): void {
  if (reportedUnknownGuids.has(moduleId)) {
    return;
  }

  reportedUnknownGuids.add(moduleId);
  console.warn(
    `[umc] module "${moduleId}" is mapped to ${moduleGuid}, but the UMC catalogue does not list it — ` +
      'treated as unlicensed. Check environment.umc.moduleIds and the module\'s products in UMC.',
  );
}

/**
 * Whether `/service/me` lists the module among the person's own modules in
 * that company. UMC lists a module there only when the company holds a live
 * licence AND the person is covered by it — company-wide or by a seat.
 */
function holdsSeat(membership: ServiceCompanyAccess, moduleCode: string): boolean {
  return membership.modules.some((module) => module.moduleCode === moduleCode);
}

/**
 * Mock catalogue, built from the SAME environment map the real branch checks
 * against, so the GUIDs always agree with the config under test. The
 * catalogue lists every mapped module for both companies — as the real
 * endpoint does — and only `heldByCompany` differs: company 1 holds
 * everything, company 2 nothing. Switching flips the Payroll tile between
 * openable and "no licence".
 */
function buildMockCatalog(companyId: number): readonly ServiceCatalogModule[] {
  return Object.entries(environment.umc.moduleIds).map(([moduleId, moduleGuid]) => ({
    modulePublicId: moduleGuid,
    moduleCode: moduleId === 'lohn' ? 'payroll' : moduleId,
    moduleLabel: moduleId,
    catalogStatus: 'active',
    heldByCompany: companyId === 1,
  }));
}

/**
 * Mock licence rows, mirroring the mock catalogue: company 1 holds every
 * mapped module (active, one month left), company 2 none.
 */
function buildMockDetails(companyId: number): readonly ServiceModule[] {
  if (companyId !== 1) {
    return [];
  }

  const expires = new Date();
  expires.setMonth(expires.getMonth() + 1);

  return Object.keys(environment.umc.moduleIds).map((moduleId) => ({
    moduleCode: moduleId,
    moduleLabel: moduleId,
    licenseStatus: 'active',
    expiresAt: expires.toISOString(),
    assignmentMode: 'company',
    seatsLimit: null,
    seatsUsed: 0,
    startsAt: null,
    licenseName: `${moduleId} base (mock)`,
    licensePublicId: '00000000-0000-0000-0000-000000000000',
  }));
}
